import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('Invalid reset link');
      setIsValidating(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await authApi.verifyResetToken(token!);
      
      if (response.success) {
        setIsValidToken(true);
        setUserEmail(response.data?.email || null);
      } else {
        setError(response.message || 'Invalid or expired reset link');
      }
    } catch (err) {
      setError('Failed to validate reset link');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link');
      return;
    }

    if (!password || !passwordConfirmation) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.resetPassword({
        token,
        password,
        password_confirmation: passwordConfirmation
      });
      
      if (response.success) {
        setSuccess(response.message || 'Password reset successfully');
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Validating Reset Link</h1>
          <p className="text-black text-base sm:text-lg">
            Please wait while we validate your password reset link...
          </p>
        </div>
        
        {/* Loading Spinner */}
        <div className="flex justify-center">
          <div className="w-8 h-8 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Invalid Reset Link</h1>
          <p className="text-black text-base sm:text-lg">
            The password reset link is invalid or has expired.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4">
          <div className="text-center">
            <Link
              to="/auth/forgot-password"
              className="inline-block bg-secondary-500 text-white py-2.5 sm:py-3 px-6 rounded-lg font-medium hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 transition-colors text-sm sm:text-base"
            >
              Request New Reset Link
            </Link>
          </div>

          <div className="text-center">
            <Link
              to="/auth/login"
              className="text-secondary-500 hover:text-secondary-600 font-medium"
            >
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Password Reset Successfully</h1>
          <p className="text-black text-base sm:text-lg">
            Your password has been updated. You will be redirected to the login page shortly.
          </p>
        </div>

        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800 text-sm">
            {success}
          </p>
        </div>

        {/* Back to Sign In Link */}
        <div className="text-center">
          <Link
            to="/auth/login"
            className="text-secondary-500 hover:text-secondary-600 font-medium"
          >
            Go to Sign In →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-0">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Reset Password</h1>
        <p className="text-black text-base sm:text-lg">
          {userEmail ? `Enter a new password for ${userEmail}` : 'Enter your new password'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Reset Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-black mb-2">
            New Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm sm:text-base"
            placeholder="Enter your new password"
            required
            disabled={isLoading}
            minLength={6}
          />
          <p className="text-xs text-gray-500 mt-1">
            Password must be at least 6 characters long
          </p>
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="password_confirmation" className="block text-sm font-medium text-black mb-2">
            Confirm New Password
          </label>
          <input
            type="password"
            id="password_confirmation"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm sm:text-base"
            placeholder="Confirm your new password"
            required
            disabled={isLoading}
          />
        </div>

        {/* Reset Password Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-secondary-500 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Resetting...
            </div>
          ) : (
            'Reset Password'
          )}
        </button>

        {/* Back to Sign In Link */}
        <div className="text-center">
          <Link
            to="/auth/login"
            className="text-secondary-500 hover:text-secondary-600 font-medium"
          >
            ← Back to Sign In
          </Link>
        </div>
      </form>
    </div>
  );
}
